/**
 * ServerAuthorityNetWorld - Server-authoritative networking mode
 *
 * In this mode:
 * - Server is authoritative over most objects
 * - Server replicates to clients based on per-client relevance
 * - Clients are passive receivers, don't replicate back to server
 */



import { Actor } from '../actors/Actor.js';
import { NetRole } from '../actors/ActorNetworking.js';
import { PlayerController } from '../actors/PlayerController.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { ClassRegistry } from '../systems/ClassRegistry.js';

import { createLagSimulatingServerFactory } from './implementations/LagSimulatingNetworkTransport.js';
import { ServerGameSession, WebSocketNetworkServer } from './implementations/ServerGameSession.js';
import {
  type ActorUpdate,
  type ClientId,
  DisconnectReason,
  type JoinParams,
  type NetId,
  type ObjectId,
  type ReplicationAck,
  type ReplicationPacket,
  type SpawnData,
  type UpdateData
} from './interface.js';
import { type IServerMapCoordinator } from './IServerMapCoordinator.js';
import { type INetObject, makeNetId } from './NetIdentity.js';
import { NetRuntime } from './NetRuntime.js';
import {
  type NetServerStartOptions,
  NetWorld
} from './NetWorld.js';
import { BinaryReader } from './ReplicationBinaryCodec.js';
import { ReplicationSerializer } from './ReplicationSerializer.js';
import { decodeRpcArgs, encodeRpcArgs } from './RpcBinaryCodec.js';

import type { GameSessionState ,
  IncomingRpc} from './interface.js';
import type { Pawn } from '../actors/Pawn.js';
import type { PlayerInfo } from '../actors/PlayerInfo.js';
import type { NetPropertyMetadata } from '../utils/serialization/decorator.js';
import type { ScenePath } from '../utils/serialization/decorator.js';

/**
 * Per-client channel state for an actor
 * Tracks what this specific client knows about this actor
 */
class ClientActorState {
  /** Transport ID for this actor on this client's channel */
  transportId: number;

  /** Has spawn message been sent to client? */
  spawned: boolean = false;

  /** Has client acknowledged spawn (channel established)? */
  channelEstablished: boolean = false;

  /** Replication tick when the most recent spawn packet was sent (first attempt or retry). */
  lastSpawnAttemptTick: number = -1;

  /** Number of spawn retransmissions sent after the initial attempt. */
  spawnRetryCount: number = 0;

  /**
   * Snapshots of the actor state (client-role view) sent to this client, keyed by tick.
   * Used as the delta base when the client confirms receipt via 'updated' ACK.
   * Entries older than `lastAckedTick` are pruned automatically.
   */
  sentSnapshots: Map<number, Record<string, unknown>> = new Map();

  /**
   * The most recently confirmed replication tick for this actor on this client.
   * - `-1` while awaiting spawn ACK.
   * - Set to the spawn packet's tick when the spawn ACK arrives.
   * - Advanced on each 'updated' ACK.
   */
  lastAckedTick: number = -1;

  /** RPCs queued until channel is established. */
  pendingRPCs: QueuedRPC[] = [];

  /** Is this actor currently relevant to this client? */
  isRelevant: boolean = true;

  /** Was this actor relevant last tick? */
  wasRelevant: boolean = false;

  /** Ticks between spawn retransmissions (~0.16 s at 60 Hz). */
  static readonly SPAWN_RETRY_INTERVAL_TICKS = 10;
  /** Give up after this many retransmissions (~3 s at 60 Hz). */
  static readonly SPAWN_MAX_RETRIES = 20;
  /**
   * Hard cap on the sentSnapshots ring buffer.
   * Must be at least 2: one slot is permanently occupied by the pinned lastAckedTick
   * snapshot, so a value of 1 would leave no room for new snapshots.
   */
  static readonly MAX_SNAPSHOTS = 16;

  constructor(transportId: number) {
    this.transportId = transportId;
  }

  /**
   * The confirmed snapshot to use as the delta base for the next send.
   * Returns `null` only before spawn ACK (when `lastAckedTick < 0`).
   * Once the channel is established the pinned-eviction policy guarantees
   * `sentSnapshots` always contains an entry for `lastAckedTick`.
   */
  getDeltaBase(): Record<string, unknown> | null {
    if (this.lastAckedTick < 0) return null;
    const snapshot = this.sentSnapshots.get(this.lastAckedTick);
    if (!snapshot) {
      throw new Error(
        `[ServerAuthorityNetWorld] Invariant violated: no snapshot for lastAckedTick ${this.lastAckedTick} ` +
        `(transport ${this.transportId})`
      );
    }
    return snapshot;
  }

  /** Store a snapshot for `tick` and prune entries confirmed obsolete. */
  recordSentSnapshot(tick: number, snapshot: Record<string, unknown>): void {
    this.sentSnapshots.set(tick, snapshot);
    for (const t of this.sentSnapshots.keys()) {
      if (t < this.lastAckedTick) this.sentSnapshots.delete(t);
    }
    // Evict oldest non-pinned entry when over the cap.
    // lastAckedTick is pinned and must never be evicted — it is the delta base
    // for all in-flight packets until a newer ACK arrives.
    while (this.sentSnapshots.size > ClientActorState.MAX_SNAPSHOTS) {
      let oldest = Infinity;
      for (const t of this.sentSnapshots.keys()) {
        if (t !== this.lastAckedTick && t < oldest) oldest = t;
      }
      if (oldest === Infinity) break;  // only the pinned entry remains
      this.sentSnapshots.delete(oldest);
    }
  }

  /** Called when an 'updated' ACK for `tick` is received. */
  onUpdateAcked(tick: number): void {
    // Only advance lastAckedTick if the snapshot for that tick still exists.
    // ACKs for evicted ticks are ignored so the delta base is never lost.
    if (tick > this.lastAckedTick && this.sentSnapshots.has(tick)) {
      this.lastAckedTick = tick;
      for (const t of this.sentSnapshots.keys()) {
        if (t < this.lastAckedTick) this.sentSnapshots.delete(t);
      }
    }
  }
}

/**
 * RPC waiting for per-actor channel establishment (pre-spawn queue on ClientActorState).
 */
interface QueuedRPC {
  methodName: string;
  argsPayload: Uint8Array;
  reliable: boolean;
  queuedAt: number;
  netSubId?: string;
}

/**
 * Fully-encoded RPC accumulated during a tick and flushed to the wire at the end of the tick,
 * after replication packets have been sent.
 */
interface OutboundRPC {
  objectId: ObjectId;
  methodName: string;
  argsPayload: Uint8Array;
  reliable: boolean;
  /** `null` = broadcast to all connected clients. */
  targetClients: ClientId[] | null;
  netSubId?: string;
}

/**
 * Per-client channel management
 * Each client has independent transport ID assignment
 */
class ClientChannelState {
  clientId: string;
  nextTransportId: number = 1;

  /** Bidirectional mappings for this client */
  actorToTransportId: Map<Actor, number> = new Map();
  transportIdToActor: Map<number, Actor> = new Map();

  constructor(clientId: string) {
    this.clientId = clientId;
  }

  /**
   * Assign a transport ID to an actor for this client
   */
  assignTransportId(actor: Actor): number {
    let transportId = this.actorToTransportId.get(actor);
    if (transportId === undefined) {
      transportId = this.nextTransportId++;
      this.actorToTransportId.set(actor, transportId);
      this.transportIdToActor.set(transportId, actor);
    }
    return transportId;
  }

  /**
   * Get transport ID for an actor (if assigned)
   */
  getTransportId(actor: Actor): number | undefined {
    return this.actorToTransportId.get(actor);
  }

  /**
   * Get actor by transport ID
   */
  getActor(transportId: number): Actor | undefined {
    return this.transportIdToActor.get(transportId);
  }

  /**
   * Remove actor from channel
   */
  removeActor(actor: Actor): void {
    const transportId = this.actorToTransportId.get(actor);
    if (transportId !== undefined) {
      this.actorToTransportId.delete(actor);
      this.transportIdToActor.delete(transportId);
    }
  }
}

/**
 * Server-side replication state for an actor
 * Tracks per-client state for this actor
 */
class ActorReplicationState {
  actor: Actor;

  /** Per-client replication state */
  clientStates: Map<string, ClientActorState> = new Map();

  constructor(actor: Actor) {
    this.actor = actor;
  }

  /**
   * Get or create client state for this actor
   */
  getOrCreateClientState(clientId: string, transportId: number): ClientActorState {
    let state = this.clientStates.get(clientId);
    if (!state) {
      state = new ClientActorState(transportId);
      this.clientStates.set(clientId, state);
    }
    return state;
  }

  /**
   * Remove client state
   */
  removeClientState(clientId: string): void {
    this.clientStates.delete(clientId);
  }
}

/**
 * Server-authoritative networking implementation
 */
/** Server-only: tracks the current stage of a server-side map transition. */
type MapTransitionState = 'idle' | 'transitioning' | 'ready';

export class ServerAuthorityNetWorld extends NetWorld<ServerGameSession> implements IServerMapCoordinator {
  /** Server-only: Map of clientId to PlayerController actor */
  private playerControllers: Map<string, PlayerController> = new Map();

  /** Server-only: Map of clientId to player pawn actor */
  private playerPawns: Map<string, Pawn> = new Map();

  /** Server-only: Track which clients have loaded the current map */
  private clientsWithMapLoaded: Set<string> = new Set();

  private currentScenePath: ScenePath = '';

  /** Server-only: Per-client channel state */
  private clientChannels: Map<string, ClientChannelState> = new Map();

  /**
   * Server-only: Current map-transition stage.
   * - `'idle'` — no transition in progress.
   * - `'transitioning'` — server is loading the new scene; joins are deferred.
   * - `'ready'` — new scene loaded; deferred joins can proceed.
   */
  private mapTransitionState: MapTransitionState = 'idle';

  /**
   * Server-only: Clients that joined while `mapTransitionState === 'transitioning'`.
   * They are flushed once the server sets the state to `'ready'`.
   */
  private deferredJoinQueue: { clientId: string; joinParams: Record<string, unknown> }[] = [];

  /**
   * Server-only: Client IDs that were active when a map transition started.
   * Used by `onServerMapReady()` to re-spawn controllers and send `requestLoadMap`.
   */
  private preservedClientIds: string[] = [];

  /** Server-only: Per-actor replication state */
  private actorReplicationStates: Map<Actor, ActorReplicationState> = new Map();

  /** Server-only: Current tick number for replication */
  private replicationTick: number = 0;

  /** Outbound RPCs accumulated this tick; flushed to the wire at the end of tick(). */
  private outboundRPCQueue: OutboundRPC[] = [];

  constructor(gameId: string) {
    super(gameId);
    console.log('[ServerAuthorityNetWorld] Using server-authority mode');
  }

  /**
   * Returns `this` as the {@link IServerMapCoordinator} for server-authoritative mode.
   * @returns This instance.
   */
  public override getMapCoordinator(): IServerMapCoordinator {
    return this;
  }

  /**
   * Called by {@link BaseGameLoop.openLevel} once the new scene has fully loaded on the server.
   * Sets `mapTransitionState` to `'ready'`, re-spawns temporary controllers for any clients
   * that were active before the map transition, and flushes the deferred-join queue.
   */
  public override onServerMapReady(): void {
    this.mapTransitionState = 'ready';

    // Re-spawn minimal controllers for preserved clients and request them to load the new map.
    // Their PlayerInfo actors are still in the world; linking happens in onClientMapLoaded().
    if (this.preservedClientIds.length > 0) {
      console.log(`[ServerAuthorityNetWorld] Map ready — re-spawning controllers for ${this.preservedClientIds.length} preserved client(s)`);
      const preserved = this.preservedClientIds.splice(0);
      for (const clientId of preserved) {
        void this.respawnControllerForTransition(clientId);
      }
    }

    // Flush any clients that joined while the server was transitioning
    console.log(`[ServerAuthorityNetWorld] Map ready — flushing ${this.deferredJoinQueue.length} deferred join(s)`);
    const deferred = this.deferredJoinQueue.splice(0);
    for (const { clientId, joinParams } of deferred) {
      void this.handlePlayerJoinedOnServer(clientId, joinParams);
    }
  }

  /**
   * Server-only: Spawns a fresh `PlayerController` for a client that was active during
   * a map transition.  The controller is registered and sent a `requestLoadMap` RPC;
   * PlayerInfo will be linked when the client confirms via `onClientMapLoaded()`.
   *
   * @param clientId - The client to re-spawn a controller for.
   */
  private async respawnControllerForTransition(clientId: string): Promise<void> {
    if (!this.world.gameMode) {
      console.error(`[ServerAuthorityNetWorld] No GameMode — cannot respawn controller for client ${clientId}`);
      return;
    }

    const playerController = await this.world.gameMode.spawnPlayerControllerForClient(clientId);
    if (!playerController) {
      console.error(`[ServerAuthorityNetWorld] GameMode failed to respawn controller for ${clientId}`);
      return;
    }

    playerController.replicated = true;
    playerController.setAutonomousProxy(true);
    playerController.onlyRelevantToOwner = true;
    playerController.netOwningClientId = clientId;
    this.world.addActors(playerController);
    this.playerControllers.set(clientId, playerController);

    // Request the client to load the new map; PlayerInfo is linked in onClientMapLoaded()
    this.requestLoadMap(clientId, this.currentScenePath);
  }

  /**
   * Server-side session wire-up. This NetWorld is only used on the dedicated server;
   * clients run `ClientNetWorld`.
   */
  protected override async onGameSessionSet(): Promise<void> {
    const session = this.gameSession!;

    this.flushPendingNetObjects();

    session.onPlayerJoined.add(this.onSessionPlayerJoined.bind(this));
    session.onPlayerLeft.add(this.onSessionPlayerLeft.bind(this));
    session.onSessionStateChanged.add(this.onSessionStateChanged.bind(this));
    session.onRPCReceived.add(this.handleIncomingRpc.bind(this));
    session.onReplicationAckReceived.add(this.handleReplicationAck.bind(this));

    // Send heartbeat so server is added as a client.
    await this.sendHeartbeat();

    // Get all existing clients in the session.
    this.sessionClients = await session.getClients();

    // Register console commands
    this.world.consoleManager.addCommandHandler(this);

    this.syncGameSessionInfoFromSession();
  }

  /**
   * Decode binary RPC payload and dispatch to the target actor or component.
   * Subscribed to `session.onRPCReceived` in {@link onGameSessionSet}.
   */
  private handleIncomingRpc(message: IncomingRpc, senderId?: ClientId): void {
    const { objectId, methodName, argsPayload, netSubId } = message;

    const actor = this.getLocalActorByObjectId(objectId);
    if (!actor) {
      console.warn(`[ServerAuthorityNetWorld] RPC target actor not found: ${objectId}`);
      return;
    }

    let params: NetPropertyMetadata[] | null;
    if (netSubId) {
      const component = actor.getComponents(SceneComponent).find(c => c.uuid === netSubId);
      if (!component) {
        console.warn(`[ServerAuthorityNetWorld] RPC component "${netSubId}" not found on actor ${objectId}`);
        return;
      }
      params = this.getRpcParamsForMethod(component.constructor, methodName);
    } else {
      params = this.getRpcParamsForMethod(actor.constructor, methodName);
    }

    if (!params) {
      console.error(
        `[ServerAuthorityNetWorld] RPC "${methodName}" has no param metadata — cannot decode. Dropping.`,
      );
      return;
    }

    let args: unknown[];
    try {
      args = decodeRpcArgs(new BinaryReader(argsPayload), params);
    } catch (error) {
      console.error(`[ServerAuthorityNetWorld] Failed to decode RPC "${methodName}":`, error);
      return;
    }

    this.dispatchRPCCall(actor, methodName, args, netSubId, senderId);
  }

  /**
   * Copies `IGameSession` fields into `GameSessionInfo` so replicated properties reach clients.
   */
  private syncGameSessionInfoFromSession(): void {
    const gameMode = this.world.gameMode;
    const session = this.gameSession;
    if (!gameMode || !session) {
      return;
    }
    const gameSessionInfo = gameMode.getGameSessionInfo();
    if (!gameSessionInfo) {
      return;
    }
    gameSessionInfo.sessionState = session.getState();
    gameSessionInfo.playerCount = session.getPlayerCount();
    gameSessionInfo.maxPlayers = session.getMaxPlayers();
    gameSessionInfo.countdown = session.getCountdown();
  }

  protected override onSessionStateChanged(oldState: GameSessionState, newState: GameSessionState): void {
    super.onSessionStateChanged(oldState, newState);
    this.syncGameSessionInfoFromSession();
  }

  /**
   * Start a dedicated server on the given port range and attach the game session this server process is hosting.
   * @param portRange - [minPort, maxPort] to try
   * @returns The port that was successfully bound
   */
  public override async startServer(portRange: [number, number], options: NetServerStartOptions = {}): Promise<number> {
    if (!NetRuntime.isDedicatedServer()) {
      throw new Error('[ServerAuthorityNetWorld] startServer() can only be called on a dedicated server');
    }

    const lagSimulationConfig = options.lagSimulationConfig;
    const session = new ServerGameSession({
      gameId: this.gameId,
      sessionName: 'server-session',
      serverFactory: lagSimulationConfig?.enabled
        ? createLagSimulatingServerFactory(() => new WebSocketNetworkServer(), lagSimulationConfig)
        : undefined
    });
    const port = await session.startListening(portRange);
    await this.attachGameSession(session);

    console.log(`[ServerAuthorityNetWorld] Server started on port ${port}`);
    return port;
  }

  /**
   * Dispatch server-side join handling. Base just logs; we hook into the
   * server's player-spawn pipeline here.
   */
  protected override async onSessionPlayerJoined(clientId: string, joinParams: JoinParams): Promise<void> {
    super.onSessionPlayerJoined(clientId, joinParams);
    await this.handlePlayerJoinedOnServer(clientId, joinParams);
    this.syncGameSessionInfoFromSession();
  }

  /**
   * Dispatch server-side leave handling.
   */
  protected override onSessionPlayerLeft(clientId: string): void {
    super.onSessionPlayerLeft(clientId);
    this.handlePlayerLeftOnServer(clientId);
    this.syncGameSessionInfoFromSession();
  }

  /**
   * Get the remote role that should be replicated to a specific client.
   * Downgrades AutonomousProxy to SimulatedProxy for non-owning clients.
   * @param actor The actor to get the remote role for
   * @param clientId The client ID to get the remote role for
   * @returns The remote role for this client
   */
  private getRemoteRoleForClient(actor: Actor, clientId: string): NetRole {
    const owningClientId = actor.netOwningClientId;
    const configuredRemoteRole = actor.netRemoteRole;

    if (!owningClientId || clientId === owningClientId) {
      // This is the owning client - return configured remote role
      return configuredRemoteRole;
    }

    // Non-owning client: downgrade AutonomousProxy to SimulatedProxy
    if (configuredRemoteRole === NetRole.AutonomousProxy) {
      return NetRole.SimulatedProxy;
    }

    return configuredRemoteRole;
  }

  /**
   * Get or create client channel state
   */
  private getOrCreateClientChannel(clientId: string): ClientChannelState {
    let channel = this.clientChannels.get(clientId);
    if (!channel) {
      channel = new ClientChannelState(clientId);
      this.clientChannels.set(clientId, channel);
    }
    return channel;
  }

  /**
   * Get or create actor replication state
   */
  private getOrCreateActorReplicationState(actor: Actor): ActorReplicationState {
    let state = this.actorReplicationStates.get(actor);
    if (!state) {
      state = new ActorReplicationState(actor);
      this.actorReplicationStates.set(actor, state);
    }
    return state;
  }

  /**
   * Send replication packet to a specific client
   */
  private sendReplicationPacket(clientId: string, packet: ReplicationPacket): void {
    const session = this.gameSession;
    if (!session) {
      console.warn('[ServerAuthorityNetWorld] Cannot send replication packet: no game session');
      return;
    }
    session.sendReplicationPacket(clientId, packet);
  }

  /**
   * Handle replication acknowledgment from client
   * Updates lastAckedState and lastAckedTick, establishes channels
   */
  private handleReplicationAck(ack: ReplicationAck, clientId: string): void {
    if (!NetRuntime.isDedicatedServer()) {
      return;
    }

    for (const actorAck of ack.actors) {
      const { transportId, ackType } = actorAck;

      // Find the actor by transport ID in the specific client's channel
      // Transport IDs are per-client, not global!
      const channel = this.clientChannels.get(clientId);
      if (!channel) {
        console.warn(`[ServerAuthorityNetWorld] Received ack from unknown client: ${clientId}`);
        continue;
      }

      const foundActor = channel.transportIdToActor.get(transportId);
      if (!foundActor) {
        console.warn(`[ServerAuthorityNetWorld] Received ack for unknown transport ID ${transportId} from client ${clientId}`);
        continue;
      }

      // Get the actor's replication state
      const actorState = this.actorReplicationStates.get(foundActor);
      if (!actorState) {
        console.warn('[ServerAuthorityNetWorld] No replication state for actor');
        continue;
      }

      const clientState = actorState.clientStates.get(clientId);
      if (!clientState) {
        console.warn('[ServerAuthorityNetWorld] No client state for actor');
        continue;
      }

      if (ackType === 'spawned') {
        // Channel is now established - client has the actor and can receive RPCs
        clientState.channelEstablished = true;
        // Record the spawn packet's tick as the first confirmed delta base.
        if (ack.tick > clientState.lastAckedTick) {
          clientState.lastAckedTick = ack.tick;
        }
        this.processQueuedRPCs(foundActor, clientId, clientState);
      } else if (ackType === 'updated') {
        clientState.onUpdateAcked(ack.tick);
      }
    }
  }

  /**
   * Process queued RPCs for an actor after channel establishment
   * Sends all queued RPCs and removes stale ones
   */
  private processQueuedRPCs(actor: Actor, clientId: string, clientState: ClientActorState): void {
    const now = Date.now();
    const staleTimeout = 5000; // 5 seconds

    // Filter out stale RPCs
    const validRPCs = clientState.pendingRPCs.filter(rpc => {
      if (now - rpc.queuedAt > staleTimeout) {
        console.warn(`[ServerAuthorityNetWorld] Dropping stale RPC ${rpc.methodName} for actor ${actor.uuid}`);
        return false;
      }
      return true;
    });

    // Enqueue all valid RPCs for the end-of-tick flush
    for (const rpc of validRPCs) {
      this.enqueueRPCToClient(actor, clientId, rpc.methodName, rpc.argsPayload, rpc.reliable, rpc.netSubId);
    }

    // Clear the queue
    clientState.pendingRPCs = [];
  }

  /**
   * Push an RPC for a single client onto the outbound queue.
   * The queue is flushed to the wire at the end of `tick()`, after replication packets.
   */
  private enqueueRPCToClient(
    actor: Actor,
    clientId: string,
    methodName: string,
    argsPayload: Uint8Array,
    reliable: boolean = true,
    netSubId?: string
  ): void {
    this.outboundRPCQueue.push({
      objectId: actor.getNetObjectId(),
      methodName,
      argsPayload,
      reliable,
      targetClients: [clientId],
      netSubId,
    });
  }

  /**
   * Flush all RPCs accumulated this tick to the wire.
   * Called at the end of `tick()`, after replication packets have been sent.
   */
  private flushOutboundRPCQueue(): void {
    if (!this.gameSession || this.outboundRPCQueue.length === 0) {
      return;
    }
    for (const rpc of this.outboundRPCQueue) {
      this.gameSession.sendClientRpc(
        rpc.objectId,
        rpc.methodName,
        rpc.argsPayload,
        rpc.reliable,
        rpc.targetClients,
        rpc.netSubId,
      );
      this.netStats.numOutgoingRPCs++;
    }
    this.outboundRPCQueue = [];
  }

  /**
   * Server-to-client RPC entry point.
   * Resolves param metadata, encodes args to binary, then queues (until the per-client
   * channel is established) or sends immediately.
   */
  public override sendClientRPC(
    objectId: ObjectId,
    methodName: string,
    args: unknown[],
    reliable: boolean = true,
    targetClients: ClientId[] | null,
    netSubId?: string
  ): void {
    // Find the actor by objectId
    const actor = this.getLocalActorByObjectId(objectId);
    if (!actor) {
      console.warn(`[ServerAuthorityNetWorld] Cannot send RPC: actor not found for objectId ${objectId}`);
      return;
    }

    // Resolve param metadata (needed to encode args)
    let params: NetPropertyMetadata[] | null;
    if (netSubId) {
      const component = actor.getComponents(SceneComponent).find(c => c.uuid === netSubId);
      params = component ? this.getRpcParamsForMethod(component.constructor, methodName) : null;
    } else {
      params = this.getRpcParamsForMethod(actor.constructor, methodName);
    }
    if (!params) {
      console.error(
        `[ServerAuthorityNetWorld] RPC "${methodName}" on ${actor.constructor.name} has no param metadata. ` +
        'Add params to the RPC decorator or ensure the property metadata generator has run.',
      );
      return;
    }

    const argsPayload = encodeRpcArgs(args, params);

    // Determine target clients
    const clients = targetClients ?? Object.keys(this.clientChannels);

    // For each target client, check if channel is established
    for (const clientId of clients) {
      let actorState = this.actorReplicationStates.get(actor);
      if (!actorState) {
        // Actor not replicated yet, create state and queue the RPC
        console.log(`[ServerAuthorityNetWorld] Creating replication state for actor ${actor.uuid} to queue RPC ${methodName}`);
        actorState = new ActorReplicationState(actor);
        this.actorReplicationStates.set(actor, actorState);
      }

      const clientState = actorState.clientStates.get(clientId);
      if (!clientState || !clientState.channelEstablished) {
        // Channel not established, queue the RPC
        if (!clientState) {
          // Create client state if it doesn't exist
          const channel = this.getOrCreateClientChannel(clientId);
          const transportId = channel.assignTransportId(actor);
          const newClientState = actorState.getOrCreateClientState(clientId, transportId);
          newClientState.pendingRPCs.push({ methodName, argsPayload, reliable, queuedAt: Date.now(), netSubId });
        } else {
          clientState.pendingRPCs.push({ methodName, argsPayload, reliable, queuedAt: Date.now(), netSubId });
        }
      } else {
        // Channel established — enqueue for end-of-tick flush.
        this.enqueueRPCToClient(actor, clientId, methodName, argsPayload, reliable, netSubId);
      }
    }
  }

  /**
   * Server-only: Set the current scene path
   * This will be replicated to clients via GameSessionInfo
   * @param mapName - The name of the map/scene
   */
  public override setScenePath(scenePath: ScenePath): void {
    if (!NetRuntime.isDedicatedServer()) {
      console.warn('[ServerAuthorityNetWorld] setScenePath can only be called on server');
      return;
    }

    console.log(`[ServerAuthorityNetWorld] setScenePath called with: "${scenePath}" (type: ${typeof scenePath}, length: ${scenePath?.length ?? 'N/A'})`);

    const oldScenePath = this.currentScenePath;
    this.currentScenePath = scenePath;
    if (oldScenePath === scenePath) {
      return;
    }

    // Clear the set of clients with map loaded since we changed maps
    this.clientsWithMapLoaded.clear();

    // Begin the transition — defer any client joins until the new scene is ready.
    this.mapTransitionState = 'transitioning';
    this.deferredJoinQueue = [] as { clientId: string; joinParams: Record<string, unknown> }[];

    // When transitioning from an existing map, clean up all player controllers and pawns.
    // The PlayerInfo actors are intentionally preserved so their data survives the transition.
    if (oldScenePath !== '') {
      this.cleanupPlayersForMapTransition();
    }

    console.log(`[ServerAuthorityNetWorld] Map set to: "${scenePath}" — transition started`);
  }

  /**
   * Server-only: Destroys every active `PlayerController` and its `Pawn` in preparation
   * for a map transition.  The linked `PlayerInfo` actors remain in the world and in
   * `GameSessionInfo.activePlayers` so their data can be restored after the new map loads.
   */
  private cleanupPlayersForMapTransition(): void {
    this.preservedClientIds = [...this.playerControllers.keys()];
    console.log(`[ServerAuthorityNetWorld] Map transition cleanup — preserving ${this.preservedClientIds.length} client(s)`);

    for (const clientId of this.preservedClientIds) {
      // Destroy pawn first (unpossess happens implicitly via removeActors → endPlay)
      const pawn = this.playerPawns.get(clientId);
      if (pawn) {
        const controller = this.playerControllers.get(clientId);
        controller?.unpossess();
        this.world.removeActors(pawn);
      }

      // Destroy the controller (PlayerInfo is NOT removed — it survives the transition)
      const controller = this.playerControllers.get(clientId);
      if (controller) {
        this.world.removeActors(controller);
      }
    }

    this.playerControllers.clear();
    this.playerPawns.clear();
  }

  /**
   * Override NetID determination for server-authority mode.
   * The server uses standard NetIDs for every object it owns; static-object adoption
   * lives on the client side (see `ClientNetWorld`).
   */
  protected override determineNetIdAndOwnership(netObject: INetObject): { netId: NetId; isLocal: boolean } {
    const deviceId = netObject.getNetDeviceId();
    const clientId = netObject.getNetClientId();
    const objectId = netObject.getNetObjectId();
    const netId = makeNetId({ deviceId, clientId, objectId });
    // Server owns all objects it creates
    return { netId, isLocal: true };
  }

  /**
   * Override removeNetObject to send destroy messages to clients when the server destroys an actor.
   */
  public override removeNetObject(netObject: INetObject): void {
    if (netObject instanceof Actor) {
      const actorState = this.actorReplicationStates.get(netObject);
      if (actorState) {
        // Send destroy message to all clients that have this actor spawned
        for (const [clientId, clientState] of actorState.clientStates) {
          if (clientState.spawned) {
            const destroyPacket: ReplicationPacket = {
              tick: this.replicationTick,
              actors: [{
                transportId: clientState.transportId,
                updateType: 'destroy'
              }]
            };
            this.sendReplicationPacket(clientId, destroyPacket);
            console.log(`[ServerAuthorityNetWorld] Sent destroy for actor ${netObject.constructor.name} to client ${clientId}`);
          }
        }
        this.actorReplicationStates.delete(netObject);
      }
    }

    super.removeNetObject(netObject);
  }

  /**
   * Server tick: per-actor channel-based replication.
   * Builds a per-client replication packet with spawn/update/destroy entries
   * based on per-client relevance and channel state.
   */
  public async tick(_deltaTime: number): Promise<void> {
    if (!this.gameSession) {
      return;
    }

    // Increment replication tick
    this.replicationTick++;

    // Get connected clients for relevance checks (exclude server itself)
    const clients = await this.gameSession.getClients();
    const clientIds = Object.keys(clients).filter(id => id !== this.clientId);

    // If no clients connected, skip replication entirely (but keep stats fresh)
    if (clientIds.length === 0) {
      this.updateNetStats(0);
      return;
    }

    // Build per-client replication packets
    const perClientPackets = new Map<string, ReplicationPacket>();
    for (const clientId of clientIds) {
      perClientPackets.set(clientId, {
        tick: this.replicationTick,
        actors: []
      });
    }

    // Distinct actors that emitted at least one update payload this tick.
    // Drives the "Local Updating Objects" stat — equivalent to peer-mode's
    // `objectsToUpdate` size, but aggregated across per-client packets.
    const updatingActors = new Set<Actor>();

    // Process each replicated actor
    let processedActors = 0;
    for (const object of this.localNetObjects.values()) {
      // Only process actors
      if (!(object instanceof Actor)) {
        continue;
      }

      // Only process replicated actors
      if (!object.replicated) {
        continue;
      }

      const actor = object as Actor;
      processedActors++;

      const actorState = this.getOrCreateActorReplicationState(actor);

      // Check relevance for each client
      for (const clientId of clientIds) {
        const packet = perClientPackets.get(clientId)!;
        const clientData = clients[clientId];

        // Skip non-PlayerController actors if client hasn't loaded the map yet
        // (PlayerController must be replicated so requestLoadMap RPC can be delivered)
        const isPlayerController = actor instanceof PlayerController;
        if (!isPlayerController && !this.clientsWithMapLoaded.has(clientId)) {
          continue;
        }

        const isRelevant = actor.isNetRelevantFor(clientId, clientData);

        const channel = this.getOrCreateClientChannel(clientId);
        const clientActorState = actorState.getOrCreateClientState(
          clientId,
          channel.assignTransportId(actor)
        );

        // Update relevancy tracking
        clientActorState.wasRelevant = clientActorState.isRelevant;
        clientActorState.isRelevant = isRelevant;

        if (!isRelevant) {
          // Actor became irrelevant - send destroy if it was spawned
          // TODO: Add NetDormancy? (send a notification to clients about irrelevant actors, so they stop updating them)
          if (clientActorState.spawned) {
            packet.actors.push({
              transportId: clientActorState.transportId,
              updateType: 'destroy'
            });
            clientActorState.spawned = false;
            clientActorState.channelEstablished = false;
            clientActorState.lastAckedTick = -1;
            clientActorState.lastSpawnAttemptTick = -1;
            clientActorState.spawnRetryCount = 0;
            clientActorState.sentSnapshots.clear();
            clientActorState.pendingRPCs = [];
          }
          continue;
        }

        // Actor is relevant (packet already retrieved above)

        if (!clientActorState.spawned) {
          // Initial spawn — build and send the spawn packet entry.
          const spawnEntry = this.buildSpawnEntry(actor, clientId, clientActorState.transportId);
          if (!spawnEntry) continue;

          packet.actors.push(spawnEntry.actorUpdate);
          clientActorState.spawned = true;
          clientActorState.lastSpawnAttemptTick = this.replicationTick;
          // Store the spawn state so getDeltaBase() works once the spawn is ACKed.
          clientActorState.sentSnapshots.set(this.replicationTick, spawnEntry.clientState);
        } else if (!clientActorState.channelEstablished) {
          // Spawn sent but not yet ACKed — retransmit periodically.
          const ticksSince = this.replicationTick - clientActorState.lastSpawnAttemptTick;
          if (ticksSince >= ClientActorState.SPAWN_RETRY_INTERVAL_TICKS) {
            if (clientActorState.spawnRetryCount >= ClientActorState.SPAWN_MAX_RETRIES) {
              console.warn(
                '[ServerAuthorityNetWorld] Spawn retry limit reached for ' +
                `${actor.constructor.name} (transport ${clientActorState.transportId}) ` +
                `on client ${clientId} — giving up`
              );
              continue;
            }
            const spawnEntry = this.buildSpawnEntry(actor, clientId, clientActorState.transportId);
            if (!spawnEntry) continue;

            packet.actors.push(spawnEntry.actorUpdate);
            clientActorState.lastSpawnAttemptTick = this.replicationTick;
            clientActorState.spawnRetryCount++;
            // Replace the stale spawn-tick snapshot with the fresh one.
            clientActorState.sentSnapshots.set(this.replicationTick, spawnEntry.clientState);
          }
        } else {
          // Channel established — delta-encode from the last ACKed confirmed state.
          const remoteRoleForClient = this.getRemoteRoleForClient(actor, clientId);
          const serverLocalRole = actor.netLocalRole;
          const serverRemoteRole = actor.netRemoteRole;
          actor.netLocalRole = remoteRoleForClient;
          actor.netRemoteRole = serverLocalRole;

          const isOwner = actor.netOwningClientId !== null && actor.netOwningClientId === clientId;

          const deltaBase = clientActorState.getDeltaBase();
          if (!deltaBase) {
            console.error(
              `[ServerAuthorityNetWorld] getDeltaBase() returned null for actor ${actor.constructor.name} ` +
              `(transport ${clientActorState.transportId}) on established channel — skipping update.`
            );
            continue;
          }

          const { state, payload } = ReplicationSerializer.serializeActor(actor, deltaBase, isOwner);

          // Restore server's actual roles.
          actor.netLocalRole = serverLocalRole;
          actor.netRemoteRole = serverRemoteRole;

          if (payload) {
            packet.actors.push({
              transportId: clientActorState.transportId,
              updateType: 'update',
              data: { payload, baseTick: clientActorState.lastAckedTick } satisfies UpdateData
            });
            updatingActors.add(actor);
            clientActorState.recordSentSnapshot(this.replicationTick, state);
          }
        }
      }
    }

    // Send replication packets to clients
    for (const [clientId, packet] of perClientPackets) {
      if (packet.actors.length > 0) {
        this.sendReplicationPacket(clientId, packet);
      }
    }

    // Flush RPCs accumulated this tick (after replication so ordering is consistent).
    this.flushOutboundRPCQueue();

    this.updateNetStats(updatingActors.size);
  }

  /**
   * Serialize an actor's spawn entry (role-swapped, full state) for the given client.
   * Returns `null` if the actor is missing required registration data.
   * Used for both the initial spawn and retransmissions.
   */
  private buildSpawnEntry(
    actor: Actor,
    clientId: string,
    transportId: number,
  ): { actorUpdate: ActorUpdate; clientState: Record<string, unknown> } | null {
    if (!ClassRegistry.getClassName(actor)) {
      console.warn(`[ServerAuthorityNetWorld] Actor ${actor.constructor.name} is not registered in ClassRegistry, skipping spawn`);
      return null;
    }

    const constructionData = actor.getNetConstructionData();
    if (!constructionData) {
      console.warn(`[ServerAuthorityNetWorld] Actor ${actor.constructor.name} has no construction data`);
      return null;
    }

    // Swap roles so the client receives its local/remote perspective.
    const remoteRoleForClient = this.getRemoteRoleForClient(actor, clientId);
    const serverLocalRole = actor.netLocalRole;
    const serverRemoteRole = actor.netRemoteRole;
    actor.netLocalRole = remoteRoleForClient;
    actor.netRemoteRole = serverLocalRole;

    const isOwner = actor.netOwningClientId !== null && actor.netOwningClientId === clientId;
    const cdoSnapshot = ReplicationSerializer.getCdoSnapshot(actor, isOwner);
    const { state: clientState, payload: initialPayload } = ReplicationSerializer.serializeActor(actor, cdoSnapshot ?? {}, isOwner);

    actor.netLocalRole = serverLocalRole;
    actor.netRemoteRole = serverRemoteRole;

    const spawnData: SpawnData = {
      uuid: actor.uuid,
      constructionData,
      owningClientId: actor.netOwningClientId ?? undefined,
      initialState: initialPayload ?? undefined,
    };

    return {
      actorUpdate: { transportId, updateType: 'spawn', data: spawnData },
      clientState
    };
  }

  /**
   * Spawn PlayerController, PlayerInfo, and Pawn when player joins.
   * Subclasses may override to customise the spawn pipeline; game-mode level customisation
   * should go through `GameMode.onPlayerJoined()`.
   */
  protected async handlePlayerJoinedOnServer(clientId: string, joinParams: Record<string, unknown> = {}): Promise<void> {
    // Don't spawn player actors for the server itself
    if (clientId === this.clientId) {
      return;
    }

    // Race-condition guard: if a map transition is in progress, defer this join until the
    // new scene is ready (onServerMapReady() will flush the queue).
    if (this.mapTransitionState === 'transitioning') {
      console.log(`[ServerAuthorityNetWorld] Map transition in progress — deferring join for client ${clientId}`);
      this.deferredJoinQueue.push({ clientId, joinParams });
      return;
    }

    console.log(`[ServerAuthorityNetWorld] Server handling player join: ${clientId}`, joinParams);

    const gameMode = this.world.gameMode;
    if (!gameMode) {
      console.error(`[ServerAuthorityNetWorld] No GameMode available for client ${clientId} — disconnecting`);
      this.disconnectClient(clientId, 'No game mode available');
      return;
    }

    // Rejoin gate: if the client supplies an identityToken that matches an inactive PlayerInfo,
    // route to the rejoin path instead of a fresh join.
    const identityToken = String(joinParams.identityToken ?? '');
    if (identityToken) {
      const preservedInfo = gameMode.inactivePlayerInfos.get(identityToken);
      if (preservedInfo) {
        const result = gameMode.canPlayerRejoin(clientId, preservedInfo, joinParams);
        if (!result.allowed) {
          console.warn(`[ServerAuthorityNetWorld] Rejoin rejected for client ${clientId}: ${result.reason}`);
          this.disconnectClient(clientId, result.reason);
          return;
        }
        await this.doPlayerRejoin(clientId, preservedInfo, gameMode, identityToken);
        return;
      }
    }

    // Bouncer gate: allow GameMode to reject fresh joins before any game objects are created.
    const joinResult = gameMode.canPlayerJoin(clientId, joinParams);
    if (!joinResult.allowed) {
      console.warn(`[ServerAuthorityNetWorld] Join rejected for client ${clientId}: ${joinResult.reason}`);
      this.disconnectClient(clientId, joinResult.reason);
      return;
    }

    // Use the unified GameMode entry point.  It spawns the controller, adds it to the world,
    // and calls handleNewPlayer.  We then layer on server-only replication properties.
    const playerController = await gameMode.onPlayerJoined(clientId);

    if (!playerController) {
      console.error(`[ServerAuthorityNetWorld] GameMode.onPlayerJoined failed for ${clientId} — disconnecting`);
      this.disconnectClient(clientId, 'Failed to spawn player controller');
      return;
    }

    // Apply server-side replication properties.
    playerController.replicated = true;
    playerController.setAutonomousProxy(true);
    playerController.onlyRelevantToOwner = true;
    this.playerControllers.set(clientId, playerController);

    // Spawn PlayerInfo, record identity token, and register for map-transition preservation.
    await playerController.spawnPlayerInfo(clientId);
    const playerInfo = playerController.getPlayerInfo();
    if (playerInfo && identityToken) {
      playerInfo.identityToken = identityToken;
    }
    const gameSessionInfo = gameMode.getGameSessionInfo();
    if (playerInfo && gameSessionInfo) {
      gameSessionInfo.activePlayers.set(clientId, playerInfo);
    }

    // If the client needs to (re-)load the current map, request it now.
    // The pawn was already spawned by onPlayerJoined → handleNewPlayer and will be
    // replicated once the client establishes its channel after loading.
    if (!this.currentScenePath) {
      console.error(`[ServerAuthorityNetWorld] No map loaded on server — disconnecting client ${clientId}`);
      this.disconnectClient(clientId, 'Server has no map loaded');
      return;
    }

    console.log(`[ServerAuthorityNetWorld] Player joined: ${clientId}, currentScenePath: "${this.currentScenePath}"`);
    if (!this.clientsWithMapLoaded.has(clientId)) {
      console.log(`[ServerAuthorityNetWorld] Client ${clientId} needs to load map: ${this.currentScenePath}`);
      this.requestLoadMap(clientId, this.currentScenePath);
    }
  }

  /**
   * Server-only: Request a client to load a specific map
   * @param clientId - The client to send the request to
   * @param mapName - The name of the map to load
   */
  private requestLoadMap(clientId: string, mapName: string): void {
    console.log(`[ServerAuthorityNetWorld] Requesting client ${clientId} to load map: ${mapName}`);

    // Use the PlayerController's RPC to request map load
    const playerController = this.playerControllers.get(clientId);
    if (playerController) {
      playerController.requestLoadMap(mapName);
    } else {
      console.warn(`[ServerAuthorityNetWorld] No PlayerController found for client ${clientId}`);
    }
  }

  /**
   * Server-only: Called when a client confirms they have loaded the map.
   * For fresh joins the controller already has a `PlayerInfo`; for post-transition
   * re-joins the controller has no `PlayerInfo` yet — retrieve the preserved one from
   * `GameSessionInfo.activePlayers`, migrate if necessary, link it, then spawn a pawn.
   *
   * @param clientId - The client that finished loading.
   */
  public async onClientMapLoaded(clientId: string): Promise<void> {
    if (!NetRuntime.isDedicatedServer()) {
      return;
    }

    console.log(`[ServerAuthorityNetWorld] Client ${clientId} finished loading map`);
    this.clientsWithMapLoaded.add(clientId);

    const playerController = this.playerControllers.get(clientId);
    if (!playerController || !this.world.gameMode) {
      return;
    }

    // Delegate PlayerInfo linking, migration, and pawn spawning to GameMode.
    await this.world.gameMode.onClientMapLoaded(playerController, clientId);

    // Track whichever pawn is now possessed.
    const pawn = playerController.getPawn();
    if (pawn) {
      this.playerPawns.set(clientId, pawn);
    }
  }

  /**
   * Destroy PlayerController, Pawn, and (optionally) PlayerInfo when a player leaves.
   * If the PlayerInfo has an identityToken it is moved to `GameMode.inactivePlayerInfos`
   * so the player can rejoin later and recover their state. Any remaining actors owned
   * by the client are removed at the end.
   */
  protected handlePlayerLeftOnServer(clientId: string): void {
    console.log(`[ServerAuthorityNetWorld] Server cleaning up actors for client: ${clientId}`);
    this.clientsWithMapLoaded.delete(clientId);

    // Evict from transition queues before anything else to prevent zombie spawns
    // for clients that disconnect while a map transition is in flight.
    this.deferredJoinQueue = this.deferredJoinQueue.filter(e => e.clientId !== clientId);
    this.preservedClientIds = this.preservedClientIds.filter(id => id !== clientId);

    const pawn = this.playerPawns.get(clientId) ?? null;
    const playerController = this.playerControllers.get(clientId) ?? null;
    const gameMode = this.world.gameMode;

    // Unpossess so the pawn is released cleanly.
    if (playerController) {
      playerController.unpossess();
    }

    // Sweep per-client replication and channel state before any world.removeActors() call
    // so removeNetObject() does not send futile destroy packets to the dead socket.
    this.clientChannels.delete(clientId);
    for (const actorState of this.actorReplicationStates.values()) {
      actorState.removeClientState(clientId);
    }
    console.log(`[ServerAuthorityNetWorld] Cleaned up channel state for client: ${clientId}`);

    // Preserve or destroy PlayerInfo — must happen before onPlayerDisconnected()
    // so overrides can reliably find the preserved state in inactivePlayerInfos.
    if (playerController && gameMode) {
      const playerInfo = playerController.getPlayerInfo();
      const gameSessionInfo = gameMode.getGameSessionInfo();
      if (playerInfo) {
        // Move from activePlayers → inactivePlayerInfos keyed by identityToken.
        if (gameSessionInfo) {
          gameSessionInfo.activePlayers.delete(clientId);
        }
        if (playerInfo.identityToken) {
          const isDev = process.env.NODE_ENV === 'development';
          if (isDev) {
            console.log(`[ServerAuthorityNetWorld] Preserving PlayerInfo for client ${clientId} (token: ${playerInfo.identityToken})`);
          } else {
            console.log(`[ServerAuthorityNetWorld] Preserving PlayerInfo for client ${clientId}`);
          }
          gameMode.inactivePlayerInfos.set(playerInfo.identityToken, playerInfo);
        }
        // Remove from world so it stops replicating to other clients.
        this.world.removeActors(playerInfo);
      }
    }

    // Notify GameMode after the transfer so overrides can observe inactivePlayerInfos.
    if (gameMode) {
      gameMode.onPlayerDisconnected(clientId, pawn, DisconnectReason.Voluntary);
    }

    // Destroy pawn.
    if (pawn) {
      console.log(`[ServerAuthorityNetWorld] Destroying pawn for client: ${clientId}`);
      this.world.removeActors(pawn);
      this.playerPawns.delete(clientId);
    }

    // Destroy controller (PlayerInfo already handled above).
    if (playerController) {
      console.log(`[ServerAuthorityNetWorld] Destroying PlayerController for client: ${clientId}`);
      this.world.removeActors(playerController);
      this.playerControllers.delete(clientId);
    }

    // Find any other actors owned by this client and destroy them (e.g. spawned by gameplay code).
    const actorsToDestroy = this.world.getActorsByPredicate(
      actor => actor.netOwningClientId === clientId
    );
    if (actorsToDestroy.length > 0) {
      console.log(`[ServerAuthorityNetWorld] Destroying ${actorsToDestroy.length} actors owned by ${clientId}`);
      this.world.removeActors(...actorsToDestroy);
    }
  }

  /**
   * Handles a client that is rejoining with a preserved `PlayerInfo`.
   * Mirrors the post-transition controller-spawn pattern: spawn a bare controller,
   * put the PlayerInfo back in activePlayers, send requestLoadMap.
   * `GameMode.onClientMapLoaded` will link the PlayerInfo and spawn the pawn.
   */
  private async doPlayerRejoin(
    clientId: string,
    playerInfo: PlayerInfo,
    gameMode: import('../game/GameMode.js').GameMode,
    identityToken: string,
  ): Promise<void> {
    const isDev = process.env.NODE_ENV === 'development';
    if (isDev) {
      console.log(`[ServerAuthorityNetWorld] Rejoin for client ${clientId} (token: ${identityToken})`);
    } else {
      console.log(`[ServerAuthorityNetWorld] Rejoin for client ${clientId}`);
    }

    // Move PlayerInfo from inactive → active.
    gameMode.inactivePlayerInfos.delete(identityToken);
    playerInfo.clientId = clientId;
    this.world.addActors(playerInfo);
    const gameSessionInfo = gameMode.getGameSessionInfo();
    if (gameSessionInfo) {
      gameSessionInfo.activePlayers.set(clientId, playerInfo);
    }

    // Spawn a fresh controller without linking PlayerInfo yet —
    // GameMode.onClientMapLoaded will link it and spawn the pawn.
    const controller = await gameMode.spawnPlayerControllerForClient(clientId);
    if (!controller) {
      console.error(`[ServerAuthorityNetWorld] Rejoin: failed to spawn controller for ${clientId}`);
      this.disconnectClient(clientId, 'Failed to spawn player controller on rejoin');
      return;
    }

    controller.netOwningClientId = clientId;
    controller.replicated = true;
    controller.setAutonomousProxy(true);
    controller.onlyRelevantToOwner = true;
    this.world.addActors(controller);
    this.playerControllers.set(clientId, controller);

    if (!this.currentScenePath) {
      console.error(`[ServerAuthorityNetWorld] Rejoin: no map loaded on server — disconnecting client ${clientId}`);
      this.disconnectClient(clientId, 'Server has no map loaded');
      return;
    }

    this.requestLoadMap(clientId, this.currentScenePath);
  }

  /**
   * Server-only: Disconnect a client from the session
   * @param clientId - The client ID to disconnect
   * @param reason - The reason for disconnection
   */
  protected disconnectClient(clientId: string, reason: string): void {
    console.error(`[ServerAuthorityNetWorld] Disconnecting client ${clientId}: ${reason}`);

    // Trigger the player left handler which will clean up all actors
    this.handlePlayerLeftOnServer(clientId);

    // Note: The actual WebSocket disconnection is handled by the session manager
    // when it detects the client has been removed from the session.
    // We just need to clean up our game state here.
  }
}

